import { useEffect, useState } from "react";
import { Satellite } from "lucide-react";
import { rtctApi } from "services/rtctApi";
import {
  localText,
  orbitsText,
  readOverpasses,
  readPlan,
  untilText,
  zoneText,
} from "utils/overpasses";
import { useStableState } from "utils/stableState";

// Upcoming satellite passes (GET devices/<mac>/overpasses/): when Sentinel-2
// and Landsat image the node's spot over the next 14 days, worked out by the
// server from the node's GNSS fix (never shown) and CelesTrak's orbits. The
// button previews the node's schedule with a ground-truth packet (photo pair,
// GNSS fix, radar burst, upload) at each pass of the next 7 days, in plain
// words; only "Send to the node" sends it, as a settings request. Passes move
// slowly, so the list is asked for every hour.
const POLL_MS = 60 * 60 * 1000;
const SCHEDULE_DAYS = 7;

export function useOverpasses(mac, refresh = 0) {
  const [state, setState] = useStableState({ mac: "", data: null, error: "" });
  useEffect(() => {
    // A server (or a test mock) without the endpoint: nothing to draw.
    if (!mac || typeof rtctApi.overpasses !== "function") return undefined;
    let abort = new AbortController();
    const load = () => {
      abort.abort();
      abort = new AbortController();
      const signal = abort.signal;
      Promise.resolve(rtctApi.overpasses(mac, signal))
        .then((data) => {
          if (!signal.aborted) setState({ mac, data: readOverpasses(data), error: "" });
        })
        .catch((e) => {
          if (!signal.aborted)
            setState((old) => ({ ...old, mac, error: e?.message || "Satellite passes unavailable" }));
        });
    };
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [mac, refresh, setState]);
  return state.mac === mac ? state : { mac, data: null, error: "" };
}

export default function SatellitePasses({ mac }) {
  const [refresh, setRefresh] = useState(0);
  const { data, error } = useOverpasses(mac, refresh);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [result, setResult] = useState(null);
  useEffect(() => {
    setPlan(null);
    setResult(null);
    setProblem("");
  }, [mac]);
  const zone = zoneText(data?.tzHours);
  const now = Date.now();
  if (!data && !error) return null;

  const ask = async (dryRun) => {
    setBusy(true);
    setProblem("");
    try {
      const answer = readPlan(
        await rtctApi.schedulePasses(mac, { days: SCHEDULE_DAYS, dry_run: dryRun }),
      );
      if (!answer) throw new Error("The server's answer could not be read");
      if (dryRun) setPlan(answer);
      else {
        setResult(answer);
        setPlan(null);
        setRefresh((n) => n + 1);
      }
    } catch (e) {
      setProblem(e?.message || "The schedule could not be built");
    } finally {
      setBusy(false);
    }
  };

  const passes = data?.passes || [];
  return (
    <section className="field-panel field-passes" aria-label="Satellite passes">
      <div className="field-panel-head">
        <div>
          <h2>Satellite passes</h2>
          <p>Sentinel-2 and Landsat, next {data?.days || 14} days{zone ? ` (${zone})` : ""}</p>
        </div>
        <span className="field-state">{passes.length} passes</span>
      </div>
      <div className="field-panel-body">
        {error && <div className="field-alert">{error}</div>}
        {data?.problem && <div className="field-notice">{data.problem.detail}.</div>}
        {passes.length > 0 && (
          <div className="field-table-scroll">
            <table className="field-table" aria-label="Satellite passes">
              <thead>
                <tr>
                  <th>Satellite</th>
                  <th>When</th>
                  <th>Off nadir</th>
                  <th>Packet</th>
                </tr>
              </thead>
              <tbody>
                {passes.map((p) => (
                  <tr key={`${p.satellite}-${p.utc}`}>
                    <td>
                      <Satellite size={13} aria-hidden="true" /> {p.satellite}
                    </td>
                    <td>
                      {localText(p.local)}
                      <small className="field-passes-until"> {untilText(p.t, now)}</small>
                    </td>
                    <td>
                      {p.offNadirKm !== null ? `${p.offNadirKm.toFixed(1)} km` : "–"}
                      {p.halfSwathKm !== null && <small> of {p.halfSwathKm} km</small>}
                    </td>
                    <td>{p.inSchedule ? "Scheduled" : "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && !data.problem && passes.length === 0 && (
          <div className="field-notice">No pass within a swath of this node in the next {data.days} days.</div>
        )}
        {data?.orbits && <p className="field-caption">{orbitsText(data.orbits)}</p>}
        {passes.length > 0 && !plan && (
          <div className="field-actions">
            <button type="button" className="field-button" disabled={busy} onClick={() => ask(true)}>
              Schedule ground truth at these passes
            </button>
          </div>
        )}
        {plan && (
          <div className="field-passes-plan" aria-label="Proposed schedule">
            <p>
              {plan.unchanged
                ? "Already scheduled at these passes:"
                : `New schedule, a ground-truth packet at each pass in the next ${plan.days || SCHEDULE_DAYS} days:`}
            </p>
            <ul>
              {plan.describe.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            {plan.skipped.length > 0 && (
              <p className="field-caption">
                Left out:{" "}
                {plan.skipped.map((p) => `${p.satellite} ${localText(p.local)} (${p.why})`).join("; ")}.
              </p>
            )}
            <div className="field-actions">
              {!plan.unchanged && (
                <button type="button" className="field-button primary" disabled={busy} onClick={() => ask(false)}>
                  Send to the node
                </button>
              )}
              <button type="button" className="field-button" disabled={busy} onClick={() => setPlan(null)}>
                {plan.unchanged ? "Close" : "Cancel"}
              </button>
            </div>
          </div>
        )}
        {problem && <div className="field-alert">{problem}</div>}
        {result && (
          <div className="field-notice" role="status">
            {result.sent
              ? "Sent. The node picks it up at its next upload."
              : result.unchanged
                ? "Nothing to send: already scheduled."
                : "Not sent: the server could not reach the broker. Try again."}
          </div>
        )}
      </div>
    </section>
  );
}
